import type { Schemas } from '@npc/api-client'
import { Link } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

import { CredentialCard } from './credential-card'
import { Bars } from '../../components/bars'
import { Button } from '../../components/button'
import { AccessMethod } from './access'
import { Reach } from './reach'
import { Card } from '../../components/card'
import { Page, PageHeader, SectionTitle } from '../../components/page'
import { StatusDot, Tag } from '../../components/tag'
import { ReachNote } from './reach'
import { ago, currentValue, daysBetween, managedDays, shortDate } from '../../app/format'
import { parentName, statusTone } from '../../app/host-status'
import { T } from '../../copy/text'
import { t, type CopyId } from '../../copy'
import { useIssueHostAccess, usePreviewHostSplitPlan, useRecheckHost } from '../../app/queries'
import { probeLive } from './probe-live'
import '../hosts/hosts.css'

/** 签发临时钥匙时写进审计的那句话。谁拿的、为什么拿，契约要求必须有。 */
const ACCESS_REASON = '在机器详情页点了「给 AI 一把临时钥匙」'

type SplitPosture = NonNullable<Schemas['HostSplitPlanPreviewRequest']['posture']>
type SplitPostureCopyId =
  | 'split.posture.minimal'
  | 'split.posture.conservative'
  | 'split.posture.balanced'
  | 'split.posture.dense'
type SplitExposureCopyId =
  | 'split.exposure.domain'
  | 'split.exposure.ip_port'
  | 'split.exposure.internal_only'

const SPLIT_POSTURE_OPTIONS: Array<{ value: SplitPosture; label: CopyId }> = [
  { value: 'minimal', label: 'split.posture.minimal' },
  { value: 'conservative', label: 'split.posture.conservative' },
  { value: 'balanced', label: 'split.posture.balanced' },
  { value: 'dense', label: 'split.posture.dense' },
]

/**
 * 机器详情的样子。
 *
 * 顺序是产品原则第 2 条「术语藏在第二层」的直接体现：
 * 第一屏是**一段大白话 + 三个标签**，往下才是跑了什么、忙不忙，
 * 原始指标与技术细节一律降到小字与第二层。
 */
export function ServerView({
  host,
  metrics,
  actions,
}: {
  host: Schemas['Host']
  metrics: Schemas['MetricSeries'][]
  actions: Schemas['TimelineEntry'][]
}) {
  const issue = useIssueHostAccess(host.id)
  const [access, setAccess] = useState<Schemas['TemporaryAccess'] | null>(null)

  return (
    <Page className="hosts-theme min-h-full bg-m-bg0 text-npc-ink">
      <PageHeader
        align="start"
        breadcrumb={<T id="host.detail.breadcrumb" vars={{ name: host.name }} />}
        title={host.name}
        subtitle={<HostMeta host={host} />}
        actions={
          host.agent_only ? undefined : (
            <Button
              size="sm"
              disabled={issue.isPending}
              onClick={() => issue.mutate(ACCESS_REASON, { onSuccess: setAccess })}
            >
              <T id="host.action.temp_key" />
            </Button>
          )
        }
      />

      {/* 第一屏：左栏用途 → 连通 → 忙不忙 → 退路；右栏拆分预览（默认收起）。
          拆分是侧栏预览，不是横贯整页的中心卡。凭证、时间线仍在下面。 */}
      <div className="mb-npc-card-sm mt-npc-card-lg grid grid-cols-1 items-start gap-npc-card-sm lg:grid-cols-[minmax(0,1.3fr)_minmax(260px,1fr)]">
        <div className="flex min-w-0 flex-col gap-npc-card-sm">
          <Card padding="lg">
            <div className="text-npc-caption text-npc-ink-soft">
              <T id="host.summary.heading" />
            </div>
            <p className="m-0 mt-npc-gap-sm max-w-[780px] text-npc-section font-normal leading-[1.7]">
              {host.summary}
            </p>
            {host.labels.length > 0 || host.parent ? (
              <div className="mt-npc-grid-sm flex flex-wrap gap-[7px]">
                {host.labels.map((label) => (
                  <Tag key={label.id} tone={label.tone}>
                    {label.text}
                  </Tag>
                ))}
                {/* 从别的机器上拆出来的，必须说清「那台倒了这台也倒」——
                    RFC-001 §7.2 把这条标成必须说，否则用户会以为它是完全独立的一台 */}
                {parentName(host.parent) ? (
                  <Tag tone="warn">
                    <T id="host.carve.shared_fate" vars={{ parent_name: parentName(host.parent)! }} />
                  </Tag>
                ) : null}
              </div>
            ) : null}
          </Card>

          {host.reach && host.reach.kind !== 'ok' ? (
            <Reach
              reach={host.reach}
              reconnectAddress={host.reach.kind === 'credential' ? host.address : undefined}
            />
          ) : null}

          {host.agent_only && metrics.length === 0 ? null : (
            <Busy
              hostId={host.id}
              metrics={metrics}
              live={probeLive(host, metrics)}
              agentOnly={host.agent_only}
            />
          )}

          {host.agent_only ? null : <AccessMethod hostId={host.id} reach={host.reach} />}
        </div>

        <SplitPlanCard host={host} />
      </div>

      <div className="grid grid-cols-[1.3fr_1fr] items-start gap-npc-card-sm">
        <div className="flex flex-col gap-npc-card-sm">
          <ChildHosts children={host.children ?? []} />
          {host.workloads.length > 0 ? <Workloads workloads={host.workloads} /> : null}
        </div>

        <div className="flex flex-col gap-npc-card-sm">
          {host.agent_only ? null : <CredentialCard host={host} access={access} />}
          <RecentActions actions={actions} />
        </div>
      </div>
    </Page>
  )
}

function SplitPlanCard({ host }: { host: Schemas['Host'] }) {
  const preview = usePreviewHostSplitPlan(host.id)
  const [requirement, setRequirement] = useState('')
  const [posture, setPosture] = useState<SplitPosture>('conservative')
  const [plan, setPlan] = useState<Schemas['HostSplitPlan'] | null>(null)
  const [open, setOpen] = useState(false)
  const notices = plan ? [...(plan.warnings ?? []), ...(plan.rejected ?? [])] : []

  return (
    <Card padding="lg">
      <div className="flex flex-wrap items-start justify-between gap-npc-card">
        <div className="min-w-[260px] flex-1">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="cursor-pointer border-none bg-transparent p-0 text-left"
          >
            <SectionTitle>
              <T id="split.card.title" />
            </SectionTitle>
          </button>
          {open ? (
            <p className="m-0 mt-npc-gap-sm max-w-[680px] text-npc-caption leading-[1.7] text-npc-ink-soft">
              <T id="split.card.body" />
            </p>
          ) : null}
        </div>
        {plan ? <Tag tone={plan.verdict_tone ?? 'neutral'}>{plan.verdict_text}</Tag> : null}
        <Button
          variant="primary"
          size="sm"
          disabled={preview.isPending}
          onClick={() => {
            setOpen(true)
            preview.mutate(
              { requirement, posture },
              {
                onSuccess: setPlan,
                onError: () => setPlan(null),
              },
            )
          }}
        >
          <T id="split.action.preview_only" />
        </Button>
      </div>

      {open ? (
      <div className="mt-npc-card-sm grid grid-cols-1 items-start gap-npc-gap-sm">
        <div className="min-w-0">
          <div className="mb-npc-gap-sm flex flex-wrap items-center gap-[7px]">
            <span className="text-npc-meta text-npc-ink-softer">
              <T id="split.posture.heading" />
            </span>
            {SPLIT_POSTURE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                variant={posture === option.value ? 'primary' : 'secondary'}
                aria-pressed={posture === option.value}
                onClick={() => setPosture(option.value)}
              >
                <T id={option.label} />
              </Button>
            ))}
          </div>
          <textarea
            value={requirement}
            onChange={(event) => setRequirement(event.target.value)}
            placeholder={t('split.requirement.placeholder')}
            rows={3}
            spellCheck={false}
            aria-label={t('split.card.title')}
            className="min-h-[86px] w-full resize-y rounded-npc-card-sm border border-npc-border-soft bg-npc-surface px-npc-grid-sm py-npc-gap-sm text-npc-body-sm leading-[1.7] text-npc-ink outline-none placeholder:text-npc-ink-faint focus:border-npc-green-border"
          />
        </div>
      </div>
      ) : null}

      {open && preview.isPending ? (
        <div className="mt-npc-grid-sm text-npc-caption text-npc-ink-soft">
          <T id="split.state.pending" />
        </div>
      ) : open && preview.error ? (
        <div className="mt-npc-grid-sm rounded-npc-inset border border-npc-red-border bg-npc-red-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-red-ink">
          <T id="error.generic" />
        </div>
      ) : null}

      {open && plan ? (
        <div className="mt-npc-card-sm border-t border-npc-border-softer pt-npc-card-sm">
          {plan.degraded_text ? (
            <div className="mb-npc-grid-sm rounded-npc-inset border border-npc-amber-border bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink">
              {plan.degraded_text}
            </div>
          ) : null}

          <div className="mb-npc-grid-sm flex flex-wrap items-center gap-[7px] text-npc-caption text-npc-ink-soft">
            <span className="text-npc-meta text-npc-ink-softer">
              <T id="split.preview.provenance.heading" />
            </span>
            <Tag tone={plan.planner === 'ai' ? 'info' : 'neutral'}>{splitPlanProvenance(plan)}</Tag>
            <Tag tone="neutral">{t(splitPostureCopyId(plan.posture))}</Tag>
            {plan.provider ? (
              <span className="break-all font-npc-mono text-npc-ink-medium">{plan.provider}</span>
            ) : null}
            {plan.model ? (
              <span className="break-all font-npc-mono text-npc-ink-medium">{plan.model}</span>
            ) : null}
          </div>

          <div className="flex flex-wrap items-baseline gap-npc-gap-sm">
            <div className="text-npc-body-lg font-semibold text-npc-ink-strong">{plan.headline}</div>
            {plan.reason ? <div className="text-npc-caption text-npc-ink-soft">{plan.reason}</div> : null}
          </div>

          <div className="mt-npc-grid-sm grid gap-npc-gap-sm">
            {plan.slices.map((slice) => (
              <div
                key={slice.ref}
                className="grid grid-cols-1 gap-npc-gap-sm rounded-npc-inset border border-npc-border-soft px-npc-grid-sm py-npc-row-y lg:grid-cols-[1fr_auto_auto] lg:items-start lg:gap-npc-card-sm"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-[7px]">
                    <span className="text-npc-body-sm font-medium text-npc-ink">{slice.name}</span>
                    <Tag tone="info">{slice.runtime.toUpperCase()}</Tag>
                  </div>
                  {slice.reason ? (
                    <div className="mt-[5px] text-npc-caption leading-[1.55] text-npc-ink-soft">
                      {slice.reason}
                    </div>
                  ) : null}
                  {slice.runtime_text ? (
                    <div className="mt-[3px] text-npc-meta text-npc-ink-softer">
                      {slice.runtime_text}
                    </div>
                  ) : null}
                </div>
                <div className="whitespace-nowrap pt-[2px] text-npc-caption text-npc-ink-soft lg:text-right">
                  {t(splitExposureCopyId(slice.exposure))}
                </div>
                <div className="whitespace-nowrap pt-[2px] font-npc-mono text-npc-caption text-npc-ink-medium lg:text-right">
                  {resourceLine(slice.resources)}
                </div>
              </div>
            ))}
          </div>

          {plan.why_not_more ? (
            <div className="mt-npc-grid-sm text-npc-caption leading-[1.6] text-npc-ink-soft">
              {plan.why_not_more}
            </div>
          ) : null}

          {notices.length > 0 ? (
            <div className="mt-npc-grid-sm flex flex-col gap-npc-gap-xs">
              {notices.map((notice) => (
                <div
                  key={`${notice.code}-${notice.text}`}
                  className="text-npc-caption leading-[1.5] text-npc-ink-soft"
                >
                  {notice.text}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : open ? (
        <div className="mt-npc-grid-sm text-npc-caption text-npc-ink-soft">
          <T id="split.state.empty" />
        </div>
      ) : null}
    </Card>
  )
}

function splitPlanProvenance(plan: Schemas['HostSplitPlan']): string {
  const vars = { date: shortDate(plan.generated_at) }
  return plan.planner === 'ai'
    ? t('split.preview.provenance.by_ai', vars)
    : t('split.preview.provenance.by_rule', vars)
}

function splitPostureCopyId(posture: string): SplitPostureCopyId {
  switch (posture) {
    case 'minimal':
      return 'split.posture.minimal'
    case 'balanced':
      return 'split.posture.balanced'
    case 'dense':
      return 'split.posture.dense'
    default:
      return 'split.posture.conservative'
  }
}

function splitExposureCopyId(exposure: string): SplitExposureCopyId {
  switch (exposure) {
    case 'domain':
      return 'split.exposure.domain'
    case 'ip_port':
      return 'split.exposure.ip_port'
    default:
      return 'split.exposure.internal_only'
  }
}

function resourceLine(resources: Schemas['HostSplitResources']): string {
  return [
    formatCores(resources.cpu_cores),
    formatBytes(resources.mem_bytes),
    formatBytes(resources.disk_bytes),
  ].join(' / ')
}

function formatCores(value: number): string {
  if (Math.abs(value - Math.round(value)) < 0.01) return `${Math.round(value)}C`
  return `${value.toFixed(1)}C`
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0G'
  const gb = value / 1024 / 1024 / 1024
  if (gb >= 1024) {
    const tb = gb / 1024
    return `${formatUnit(tb)}T`
  }
  return `${formatUnit(gb)}G`
}

function formatUnit(value: number): string {
  return Math.abs(value - Math.round(value)) < 0.05 ? String(Math.round(value)) : value.toFixed(1)
}

function ChildHosts({ children }: { children: Schemas['HostSummary'][] }) {
  if (children.length === 0) return null

  return (
    <Card>
      <SectionTitle className="mb-npc-grid-sm">
        <T id="host.carve.split_count" vars={{ count: children.length }} />
      </SectionTitle>
      <div className="grid grid-cols-[1.6fr_0.8fr_0.9fr_80px] border-b border-npc-border-soft pb-npc-gap-sm text-npc-caption-sm text-npc-ink-soft">
        <span>
          <T id="host.list.col.host" />
        </span>
        <span>
          <T id="host.list.col.project" />
        </span>
        <span>
          <T id="host.list.col.where" />
        </span>
        <span>
          <T id="host.list.col.state" />
        </span>
      </div>

      <div className="flex flex-col">
        {children.map((child) => {
          const project = child.projects[0]

          return (
            <Link
              key={child.id}
              to="/hosts/$hostId"
              params={{ hostId: child.id }}
              className="grid grid-cols-[1.6fr_0.8fr_0.9fr_80px] items-center gap-npc-gap border-b border-npc-border-softer py-npc-grid-sm text-npc-caption no-underline last:border-b-0"
            >
              <div className="min-w-0">
                <div className="truncate text-npc-body-sm font-medium text-npc-ink">{child.name}</div>
                <div className="mt-[3px] font-npc-mono text-npc-meta-sm text-npc-ink-softer">
                  {child.address}
                </div>
                {/*
                  这一格原来是一句写死的「已保管」——**对每一台子机器说同一句话，
                  与那台机器的真实状态无关。** 它是从凭证卡那边借来的标签
                  （设计稿里「已保管」是给那个 `•••••••• ` 遮起来的值配的说明，
                  不是一列机器状态），而借过来之后它变成了一个恒为真的状态指示。

                  **一个永远说「没事」的状态格，比没有这一格更糟**：
                  他扫一眼三台子机器，三行都写着「已保管」，于是他相信钥匙都好着——
                  而其中一台可能正是 `reach.kind = credential`（钥匙不认了）。

                  换成真的那份：`ReachNote` 只在够不到的时候说话，够得到就整格不出现
                  （沉默是好消息）。判据是后端给的 `reach`，前端一处都不推。
                */}
                <ReachNote reach={child.reach} />
              </div>
              <span>{project ? <Tag tone="neutral">{project.name}</Tag> : null}</span>
              <span className="text-npc-ink-soft">
                {parentName(child.parent) ? (
                  <T id="host.carve.lives_on" vars={{ parent_name: parentName(child.parent)! }} />
                ) : (
                  child.region_text
                )}
              </span>
              <span>
                {child.status_text ? <Tag tone={statusTone(child)}>{child.status_text}</Tag> : null}
              </span>
            </Link>
          )
        })}
      </div>
    </Card>
  )
}

/** IP · 服务商与规格 · 托管了多久。竖线是分隔符，不是字。 */
function HostMeta({ host }: { host: Schemas['Host'] }) {
  const facts = (host.facts ?? {}) as { region?: string; cloud?: { region?: string } }
  const region = facts.region ?? facts.cloud?.region
  const vendorLine =
    host.vendor && region && host.spec_text
      ? t('host.meta.line', { vendor: host.vendor.name, region, spec: host.spec_text })
      : host.vendor?.name

  return (
    <div className="flex items-center gap-npc-gap text-npc-body text-npc-ink-soft">
      {/* 多个地址全列出来。srv01 自己就是两个公网 IP，而这儿以前只显示一个——
          另一个在界面上根本不存在。`addresses` 后端只回够得着的那些 */}
      <span className="font-npc-mono">
        {(host.addresses?.length ? host.addresses : [host.address]).join('  ')}
      </span>
      {vendorLine ? (
        <>
          <Separator />
          <span>{vendorLine}</span>
        </>
      ) : null}
      {host.created_at ? (
        <>
          <Separator />
          <span>
            <T id="host.meta.managed_days" vars={{ days: managedDays(host.created_at) }} />
          </span>
        </>
      ) : null}
      {host.agent_only ? (
        <>
          <Separator />
          <span>
            <T id="host.card.agent" />{' '}
            {host.agent_last_seen_at ? ago(host.agent_last_seen_at) : t('host.card.unknown')}
          </span>
        </>
      ) : null}
    </div>
  )
}

function Separator() {
  return (
    <span aria-hidden="true" className="text-npc-scrollbar">
      |
    </span>
  )
}

/** 上面跑着这些东西。人话在上，技术细节在下面那行小字里。 */
function Workloads({ workloads }: { workloads: Schemas['Workload'][] }) {
  return (
    <Card>
      <SectionTitle className="mb-npc-gap-lg">
        <T id="host.workloads.heading" />
      </SectionTitle>
      <div className="flex flex-col gap-npc-gap-sm">
        {workloads.map((workload) => (
          <div
            key={`${workload.kind}-${workload.name}`}
            className="flex items-center gap-npc-grid-sm rounded-npc-inset border border-npc-border-soft px-npc-grid-sm py-npc-row-y"
          >
            <StatusDot tone={workload.healthy === false ? 'warn' : 'ok'} />
            <div className="min-w-0 flex-1">
              <div className="text-npc-body-sm font-medium">{workload.summary ?? workload.name}</div>
              <div className="mt-[3px] font-npc-mono text-npc-meta-sm text-npc-ink-softer">
                {technicalLine(workload)}
              </div>
            </div>
            {/* 设计稿这一行最右边还有一个占用（「1.2 GB」），但契约里 Workload 没有
                内存字段，也没有 additionalProperties，编不出来。宁可空着也不编数据——
                需要流 0 给 Workload 补一个字段，见交付报告 */}
          </div>
        ))}
      </div>
    </Card>
  )
}

/**
 * 那行小字。设计稿里是「docker · order-api:2026.08.05」这种——
 * 容器多一个 `docker ·` 前缀，别的直接就是指纹识别出来的名字。
 */
function technicalLine(workload: Schemas['Workload']): string {
  return workload.kind === 'container' ? `docker · ${workload.name}` : workload.name
}

const METRIC_LABEL: Partial<Record<Schemas['MetricSeries']['metric'], CopyId>> = {
  cpu: 'host.metric.cpu.label',
  memory: 'host.metric.memory.label',
  disk: 'host.metric.disk.label',
}

/** 忙不忙。三条线，每条 24 根柱子。 */
function Busy({
  hostId,
  metrics,
  live,
  agentOnly,
}: {
  hostId: string
  metrics: Schemas['MetricSeries'][]
  live: boolean
  agentOnly: boolean
}) {
  const [detail, setDetail] = useState(false)
  const recheck = useRecheckHost(hostId)
  const shown = (['cpu', 'memory', 'disk'] as const)
    .map((metric) => metrics.find((series) => series.metric === metric))
    .filter((series): series is Schemas['MetricSeries'] => Boolean(series))

  return (
    <Card data-testid="host-busy">
      <div className="mb-npc-card-sm flex items-baseline justify-between gap-npc-gap-sm">
        <SectionTitle>
          <T id="host.busy.heading" />
        </SectionTitle>
        <div className="flex flex-wrap items-center gap-npc-gap-sm">
          {agentOnly ? null : (
            <Button size="sm" disabled={recheck.isPending} onClick={() => recheck.mutate()}>
              <T id="host.recheck.action" />
            </Button>
          )}
          <div className="text-npc-caption-sm text-npc-ink-soft">
            <T id="host.busy.window" />
          </div>
        </div>
      </div>
      {!agentOnly && recheck.isPending ? (
        <p className="mb-npc-gap-sm mt-0 text-npc-caption text-npc-ink-soft">
          <T id="host.recheck.running" />
        </p>
      ) : null}
      {!agentOnly && recheck.isError ? (
        <p className="mb-npc-gap-sm mt-0 text-npc-caption leading-[1.65] text-npc-amber-ink">
          <T id="host.recheck.failed" />
        </p>
      ) : null}

      {live ? (
        <>
          <div className="grid grid-cols-3 gap-npc-card">
            {shown.map((series) => {
              const label = METRIC_LABEL[series.metric]
              const value = currentValue(series.points)
              return (
                <div key={series.metric}>
                  <div className="mb-npc-gap-sm flex items-baseline gap-npc-gap-xs">
                    {detail && value !== null ? (
                      <span className="font-npc-display text-npc-stat-sm font-semibold leading-none">
                        {value}%
                      </span>
                    ) : null}
                    {series.headline ? (
                      <span className="text-npc-caption-sm text-npc-ink-soft">{series.headline}</span>
                    ) : null}
                  </div>
                  {detail ? <Bars points={series.points} /> : null}
                  {label ? (
                    <div className="mt-[7px] text-npc-meta text-npc-ink-softer">
                      <T id={label} />
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
          <button
            type="button"
            className="mt-npc-gap-sm border-none bg-transparent p-0 text-npc-caption text-npc-ink-soft"
            onClick={() => setDetail((value) => !value)}
          >
            <T id="dashboard.attention.detail_cta" />
          </button>
        </>
      ) : (
        <div data-testid="host-busy-unknown">
          <div className="mb-npc-gap-sm rounded-npc-inset border border-npc-amber-border bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink">
            <T id="host.probe.disconnected" />
          </div>
          <div className="grid grid-cols-3 gap-npc-card">
            {(['cpu', 'memory', 'disk'] as const).map((metric) => {
              const label = METRIC_LABEL[metric]
              return (
                <div key={metric}>
                  <div className="text-npc-body-sm text-npc-ink-soft">
                    <T id="activity.status.unknown" />
                  </div>
                  {label ? (
                    <div className="mt-[7px] text-npc-meta text-npc-ink-softer">
                      <T id={label} />
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </Card>
  )
}

/** 我最近在这台上做了什么。今天、昨天，再往前用短日期。 */
function RecentActions({ actions }: { actions: Schemas['TimelineEntry'][] }) {
  const [now] = useState(() => new Date())
  if (actions.length === 0) return null

  return (
    <Card>
      <SectionTitle className="mb-npc-grid-sm">
        <T id="host.recent.heading" />
      </SectionTitle>
      <div className="flex flex-col gap-npc-grid-sm">
        {actions.map((action) => (
          <div key={action.id} className="flex gap-npc-gap">
            <div className="w-[46px] shrink-0 pt-[2px] font-npc-mono text-npc-meta-sm text-npc-ink-softer">
              {whenLabel(action.at, now)}
            </div>
            <div className="text-npc-body-sm leading-[1.5]">{action.title}</div>
          </div>
        ))}
      </div>
    </Card>
  )
}

function whenLabel(at: string, now: Date): ReactNode {
  const days = daysBetween(at, now)
  if (days <= 0) return <T id="host.recent.today" />
  if (days === 1) return <T id="host.recent.yesterday" />
  return shortDate(at)
}
